import React, { useEffect, useState } from "react";
import { FiTrash2, FiEdit, FiPlus, FiSearch, FiMapPin, FiBriefcase, FiDollarSign } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";

import { fetchJobs, deleteJob } from "../../../redux/actions/jobActions";

import CreateJobs from "./CreateJobs.tsx";
import EditJobs from "./EditJobs.tsx";

const Jobs = () => {
  const dispatch = useDispatch<any>();

  const { jobs, loading, error } = useSelector((state: any) => state.job);

  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // delete modal state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    dispatch(fetchJobs());
  }, [dispatch]);

  const handleDelete = (id: any) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteJob(deleteId));
    setConfirmDeleteOpen(false);
    setDeleteId(null);
  };

  const openEdit = (job: any) => {
    setSelectedJob(job);
    setEditOpen(true);
  };

  const filteredJobs =
    jobs?.filter(
      (j: any) =>
        j.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.location?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            Job Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Post and manage career opportunities for the EBLUESYS portal
          </p>
        </div>

        <button
          onClick={() => setCreateOpen(true)}
          className="px-6 py-3 rounded-xl flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
        >
          <FiPlus className="text-lg" />
          Create New Job
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
          <input
            type="text"
            placeholder="Search by job title, department, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
        </div>
      </div>

      {loading && (
        <p className="text-blue-600 dark:text-blue-400 animate-pulse mb-4">
          Loading jobs...
        </p>
      )}
      {error && <p className="text-red-500 mb-4 font-medium">{error}</p>}

      <div className="overflow-x-auto shadow-lg rounded-2xl border border-gray-200 dark:border-gray-700">
        <table className="min-w-full bg-white dark:bg-gray-800 border-collapse">
          <thead className="bg-gray-100 dark:bg-gray-700 shadow-[0_2px_10px_rgba(0,0,0,0.05)] relative z-10">
            <tr>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">#</th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">Job Details</th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">Requirements</th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">Status</th>
              <th className="py-4 px-4 text-left font-semibold text-gray-800 dark:text-white border-b">Posted On</th>
              <th className="py-4 px-4 text-center font-semibold text-gray-800 dark:text-white border-b">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filteredJobs.map((job: any, index: number) => (
              <tr
                key={job.id}
                className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <td className="py-4 px-4 text-gray-500 dark:text-gray-400 text-sm">
                  {index + 1}
                </td>

                <td className="py-5 px-4 min-w-[220px]">
                  <div className="flex flex-col gap-1">
                    <div className="font-bold text-gray-900 dark:text-white text-base tracking-tight leading-snug">
                      {job.job_title}
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-blue-500 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 w-fit px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-800/50">
                      <FiBriefcase className="text-[10px]" />
                      <span>{job.department}</span>
                    </div>
                    <div className="flex flex-col gap-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <FiMapPin className="text-gray-400" /> {job.location} • {job.job_type}
                      </div>
                      <div className="flex items-center gap-1 font-medium text-green-600 dark:text-green-400">
                        <FiDollarSign /> {job.salary_range}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="py-5 px-4 min-w-[200px]">
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Experience</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{job.experience_required}</span>
                    <div className="mt-1 line-clamp-1 text-[11px] text-gray-500 dark:text-gray-400 italic">
                      {job.requirements}
                    </div>
                  </div>
                </td>

                <td className="py-4 px-4">
                  <span
                    className={`px-2 py-1 text-[10px] font-bold rounded-full ${
                      job.status === "active"
                        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    }`}
                  >
                    {job.status?.toUpperCase()}
                  </span>
                </td>

                <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400">
                  {new Date(job.created_at).toLocaleDateString(undefined, {
                    dateStyle: "medium",
                  })}
                </td>

                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => openEdit(job)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 dark:hover:text-white hover:bg-blue-600 hover:text-white rounded-lg transition-colors"
                    >
                      <FiEdit size={14} />
                      <span className="text-xs font-bold uppercase">Edit</span>
                    </button>

                    <button
                      onClick={() => handleDelete(job.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors"
                    >
                      <FiTrash2 size={14} />
                      <span className="text-xs font-bold uppercase">Delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredJobs.length === 0 && !loading && (
          <div className="bg-white dark:bg-gray-800 p-12 text-center">
            <div className="text-gray-400 dark:text-gray-500 text-lg">
              No job postings found matching your search.
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {createOpen && <CreateJobs close={() => setCreateOpen(false)} />}
      {editOpen && (
        <EditJobs job={selectedJob} close={() => setEditOpen(false)} />
      )}

      {/* Redesigned Delete Modal (Identical to Blog) */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                <FiTrash2 className="text-red-600 dark:text-red-400 text-xl" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                Delete Job Posting
              </h3>

              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete this job? This will remove it
                from the public "Current Opportunities" section.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDeleteOpen(false)}
                  className="flex-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>

                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition-colors font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;